import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2 } from "lucide-react";
import StateSelect from "@/components/StateSelect";

/**
 * SubmitBusinessDialog
 * Publicly open — anyone (logged-in or not) can nominate a Black-owned
 * business they know. Enters admin review queue as `pending`.
 *
 * POST /api/business-submissions/submit
 */
export default function SubmitBusinessDialog({ open, onOpenChange }) {
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    category: "",
    custom_category: "",
    city: "",
    state: "",
    website: "",
    phone: "",
    email: "",
    description: "",
    submitter_name: "",
    submitter_email: "",
    relationship: "",
    note: "",
    website_confirm: "", // honeypot
  });

  useEffect(() => {
    axios
      .get(`${API}/categories`)
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const reset = () => {
    setForm({
      name: "", category: "", custom_category: "", city: "", state: "",
      website: "", phone: "", email: "", description: "",
      submitter_name: "", submitter_email: "", relationship: "", note: "",
      website_confirm: "",
    });
    setSubmitted(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.city || !form.state ||
        !form.submitter_name || !form.submitter_email) {
      toast.error("Please fill in the required fields (*)");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/business-submissions/submit`, form);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not submit — please try again.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) setTimeout(reset, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-tbc-navy">
            Submit A Business
          </DialogTitle>
          <DialogDescription className="font-body">
            Know a Black-owned business we should feature? Nominate one — for a
            friend, family member, or a shop you love. We&apos;ll review and reach out
            to the owner to invite them onto the platform.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center space-y-4" data-testid="submit-business-success">
            <CheckCircle2 className="w-14 h-14 text-tbc-gold mx-auto" />
            <h3 className="font-heading text-2xl text-tbc-navy">
              Thanks, {form.submitter_name.split(" ")[0] || "friend"}!
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              We&apos;ve received your nomination for <strong>{form.name}</strong> and
              added it to our review queue. Our team will reach out to the owner
              soon to get them onboarded.
            </p>
            <div className="pt-2">
              <Button
                onClick={() => handleClose(false)}
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8"
                data-testid="submit-business-close"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            {/* Honeypot — hidden from real users, bots will fill it */}
            <input
              type="text"
              value={form.website_confirm}
              onChange={(e) => setForm({ ...form, website_confirm: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
              className="absolute -left-[9999px] w-px h-px opacity-0"
              aria-hidden="true"
            />

            <div className="rounded-lg bg-tbc-cream/40 px-4 py-3 border border-tbc-gold/20">
              <p className="text-xs uppercase tracking-widest text-tbc-navy/70 font-semibold">
                About the business
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business name *
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Onyx & Mane Customs"
                required
                data-testid="submit-biz-name"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <Select
                  value={form.category || undefined}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger data-testid="submit-biz-category">
                    <SelectValue placeholder="Choose a category" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(categories.length
                      ? categories
                      : ["Food & Beverage", "Other"]
                    ).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.category === "Other" && (
                  <Input
                    className="mt-2"
                    value={form.custom_category}
                    onChange={(e) => setForm({ ...form, custom_category: e.target.value })}
                    placeholder="Specify the field (e.g. DJ services)"
                    data-testid="submit-biz-custom-category"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Where is it based? *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    className="col-span-2"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="City"
                    required
                    data-testid="submit-biz-city"
                  />
                  <StateSelect
                    value={form.state}
                    onChange={(v) => setForm({ ...form, state: v })}
                    placeholder="State"
                    testId="submit-biz-state"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://..."
                  data-testid="submit-biz-website"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 555-5555"
                  data-testid="submit-biz-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business email (if you know it)
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="owner@example.com"
                data-testid="submit-biz-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What do they do?
              </label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="A sentence or two about what makes them great."
                data-testid="submit-biz-description"
              />
            </div>

            <div className="rounded-lg bg-tbc-cream/40 px-4 py-3 border border-tbc-gold/20 mt-2">
              <p className="text-xs uppercase tracking-widest text-tbc-navy/70 font-semibold">
                About you
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your name *
                </label>
                <Input
                  value={form.submitter_name}
                  onChange={(e) => setForm({ ...form, submitter_name: e.target.value })}
                  required
                  data-testid="submit-biz-submitter-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your email *
                </label>
                <Input
                  type="email"
                  value={form.submitter_email}
                  onChange={(e) => setForm({ ...form, submitter_email: e.target.value })}
                  required
                  data-testid="submit-biz-submitter-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Relationship to the business
              </label>
              <Input
                value={form.relationship}
                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                placeholder="e.g. friend of the owner, regular customer"
                data-testid="submit-biz-relationship"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anything else we should know?
              </label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={2}
                placeholder="Optional — best time to reach them, what makes them stand out, etc."
                data-testid="submit-biz-note"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                data-testid="submit-biz-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-6"
                data-testid="submit-biz-save"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit For Review
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
